import { useFormContext, useWatch } from 'react-hook-form'
import { ValidationPanel } from '@zawarski/eti-ui'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import type { ADFormData } from '../ADForm.types'
import {
  STRUCTURAL_HAZARD_TYPES,
  NON_STRUCTURAL_HAZARD_TYPES,
  GEOTECHNICAL_HAZARD_TYPES,
  STRUCTURAL_HAZARD_LABELS,
  NON_STRUCTURAL_HAZARD_LABELS,
  GEOTECHNICAL_HAZARD_LABELS,
} from '../ADForm.types'

interface ADValidationPanelProps {
  isSubmitting: boolean
  hasSaveAttempted: boolean
}

const STRUCTURAL_NON_OTHER = STRUCTURAL_HAZARD_TYPES.filter((t) => t !== 'OTHER')
const NON_STRUCTURAL_NON_OTHER = NON_STRUCTURAL_HAZARD_TYPES.filter((t) => t !== 'OTHER')
const GEOTECHNICAL_NON_OTHER = GEOTECHNICAL_HAZARD_TYPES.filter((t) => t !== 'OTHER')

/*
 * Wrapper component data flow:
 *
 *   useFormContext() + useWatch([...fields])
 *           │
 *           ▼
 *   evaluated booleans (isValid per field)
 *           │
 *           ▼
 *   groups: ValidationGroup[]
 *           │
 *           ▼
 *   <div className="fixed right-4 z-40 top-3|bottom-20">
 *     <ValidationPanel
 *       groups={groups}
 *       hasSaveAttempted={hasSaveAttempted}
 *       isMobile={isMobile}
 *     />
 *   </div>
 */

export default function ADValidationPanel({ isSubmitting, hasSaveAttempted }: ADValidationPanelProps) {
  const { control } = useFormContext<ADFormData>()
  // Watch only validated fields; avoids re-renders on address/comment/photo field changes.
  // Cast is safe -- form is always initialized with getDefaultDetailedFormData().
  const [
    inspectorId, inspectionDateTime, county,
    typeOfConstruction, primaryOccupancy,
    hazards, structuralHazards, nonStructuralHazards, geotechnicalHazards,
    estimatedBuildingDamage, postingStatus,
  ] = useWatch({
    control,
    name: [
      'inspectorId', 'inspectionDateTime', 'county',
      'typeOfConstruction', 'primaryOccupancy',
      'hazards', 'structuralHazards', 'nonStructuralHazards', 'geotechnicalHazards',
      'estimatedBuildingDamage', 'postingStatus',
    ] as const,
  })
  const values = {
    inspectorId, inspectionDateTime, county,
    typeOfConstruction, primaryOccupancy,
    hazards, structuralHazards, nonStructuralHazards, geotechnicalHazards,
    estimatedBuildingDamage, postingStatus,
  } as unknown as ADFormData
  const isMobile = useIsMobile()

  if (isSubmitting) return null

  const groups = [
    {
      groupLabel: 'Inspection',
      fields: [
        { label: 'Inspector ID', isValid: (values.inspectorId ?? '').length > 0 },
        { label: 'Inspection Date/Time', isValid: (values.inspectionDateTime ?? '').length > 0 },
      ],
    },
    {
      // County is required by the assessments API. Both the parcel lookup and the address
      // autocomplete fill it; it can also be typed directly in the Address section.
      groupLabel: 'Address',
      fields: [
        { label: 'County', isValid: (values.county ?? '').trim().length > 0 },
      ],
    },
    {
      groupLabel: 'Building',
      fields: [
        { label: 'Construction Type', isValid: values.typeOfConstruction !== undefined },
        { label: 'Primary Occupancy', isValid: values.primaryOccupancy !== undefined },
      ],
    },
    {
      groupLabel: 'Evaluation',
      fields: [
        {
          label: 'Collapse or partial collapse',
          isValid: values.hazards.collapseOrOffFoundation !== undefined,
        },
        ...STRUCTURAL_NON_OTHER.map((type) => ({
          label: STRUCTURAL_HAZARD_LABELS[type],
          isValid: (values.structuralHazards ?? []).find((h) => h.hazardType === type)?.severity !== undefined,
        })),
        ...NON_STRUCTURAL_NON_OTHER.map((type) => ({
          label: NON_STRUCTURAL_HAZARD_LABELS[type],
          isValid: (values.nonStructuralHazards ?? []).find((h) => h.hazardType === type)?.severity !== undefined,
        })),
        ...GEOTECHNICAL_NON_OTHER.map((type) => ({
          label: GEOTECHNICAL_HAZARD_LABELS[type],
          isValid: (values.geotechnicalHazards ?? []).find((h) => h.hazardType === type)?.severity !== undefined,
        })),
        {
          label: 'Estimated Building Damage',
          isValid: values.estimatedBuildingDamage !== undefined,
        },
      ],
    },
    {
      groupLabel: 'Posting',
      fields: [
        { label: 'Posting Status', isValid: values.postingStatus !== undefined },
      ],
    },
  ]

  return (
    <div className={cn('fixed right-4 z-40', isMobile ? 'bottom-20' : 'top-3')}>
      <ValidationPanel
        groups={groups}
        hasSaveAttempted={hasSaveAttempted}
        isMobile={isMobile}
      />
    </div>
  )
}
